import { forwardRef, useId, useState } from "react";
import { cx } from "@/lib/cx.js";

/*
 * Form primitives.
 *
 * The contract is the one index.css already defines and the markup already used:
 * a .field-group wrapper gains .is-invalid, which styles the control's border and
 * reveals the .field-error paragraph. That means react-hook-form plugs straight
 * in — pass its message to <Field error={…}> and nothing else changes.
 *
 *   <Field label="Email" error={errors.email?.message} hint="Also your username">
 *       <Input {...register("email")} type="email" placeholder="you@company.com" />
 *   </Field>
 *
 * Two control sizes, matching the markup:
 *   size="sm"  →  .field      compact, for toolbars and filter bars
 *   default    →  .field-lg    comfortable, for modals and settings forms
 */

/* ---- Wrapper ------------------------------------------------------------- */

export function Field({ label, error, hint, optional, htmlFor, className, children }) {
    return (
        <div className={cx("field-group", error && "is-invalid", className)}>
            {label && (
                <Label htmlFor={htmlFor} optional={optional}>
                    {label}
                </Label>
            )}

            {children}

            {/* Always rendered so the reveal is pure CSS (.is-invalid .field-error),
                which keeps the layout from jumping as the message appears. */}
            <p className="field-error" role={error ? "alert" : undefined}>
                <svg
                    className="size-3.5 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    aria-hidden="true"
                >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v4.5M12 16h.01" />
                </svg>
                <span>{error}</span>
            </p>

            {hint && !error && <p className="hint">{hint}</p>}
        </div>
    );
}

export function Label({ htmlFor, optional, className, children }) {
    return (
        <label htmlFor={htmlFor} className={cx("label", className)}>
            {children}
            {optional && <span className="label-optional">optional</span>}
        </label>
    );
}

/* ---- Controls ------------------------------------------------------------ */

export const Input = forwardRef(function Input({ size, className, ...rest }, ref) {
    return <input ref={ref} className={cx(size === "sm" ? "field" : "field-lg", className)} {...rest} />;
});

export const Textarea = forwardRef(function Textarea({ rows = 3, className, ...rest }, ref) {
    return <textarea ref={ref} rows={rows} className={cx("textarea", className)} {...rest} />;
});

export const Select = forwardRef(function Select({ size, className, children, ...rest }, ref) {
    return (
        <select ref={ref} className={cx(size === "sm" ? "field" : "field-lg", className)} {...rest}>
            {children}
        </select>
    );
});

/** Input with a leading icon — the auth-page style (.input + .input-icon). */
export const IconInput = forwardRef(function IconInput({ icon, className, ...rest }, ref) {
    return (
        <div className="relative">
            <span className="input-icon">{icon}</span>
            <input ref={ref} className={cx("input", className)} {...rest} />
        </div>
    );
});

/** Search box with a magnifier and the compact .field height. */
export const SearchInput = forwardRef(function SearchInput({ className, ...rest }, ref) {
    return (
        <div className="relative flex items-center">
            <svg
                className="pointer-events-none absolute left-2.5 size-4 text-slate-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
            >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.2-3.2" />
            </svg>
            <input
                ref={ref}
                type="search"
                className={cx("field pr-3 pl-8 placeholder:text-slate-400", className)}
                {...rest}
            />
        </div>
    );
});

/**
 * Password field with a reveal toggle.
 *
 * `icon` renders the auth-page leading-icon variant; without it you get the plain
 * .field-lg used in modals and settings.
 */
export const PasswordInput = forwardRef(function PasswordInput({ icon, className, ...rest }, ref) {
    const [visible, setVisible] = useState(false);

    return (
        <div className="relative">
            {icon && <span className="input-icon">{icon}</span>}

            <input
                ref={ref}
                type={visible ? "text" : "password"}
                className={cx(icon ? "input" : "field-lg", "pr-11", className)}
                {...rest}
            />

            <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? "Hide password" : "Show password"}
                className="absolute top-1/2 right-2.5 grid size-7 -translate-y-1/2 place-items-center rounded text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
                <svg
                    className="size-4.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    aria-hidden="true"
                >
                    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                    {!visible && <path d="m3 3 18 18" />}
                </svg>
            </button>
        </div>
    );
});

/**
 * Toggle switch. Controlled or uncontrolled — react-hook-form's register works
 * because it is a real checkbox underneath.
 *
 * `tone` colours the on state: emerald for a normal setting, amber for one that
 * needs care, violet for a platform entitlement.
 */
const SWITCH_TONE = {
    emerald: "peer-checked:bg-emerald-500",
    brand: "peer-checked:bg-brand-500",
    amber: "peer-checked:bg-amber-500",
    violet: "peer-checked:bg-violet-500",
};

export const Switch = forwardRef(function Switch(
    { label, hint, tone = "emerald", disabled, className, id, ...rest },
    ref,
) {
    const autoId = useId();
    const inputId = id ?? autoId;

    const control = (
        <span className="relative inline-flex shrink-0">
            <input
                ref={ref}
                id={inputId}
                type="checkbox"
                disabled={disabled}
                className="peer sr-only"
                {...rest}
            />
            <span
                className={cx(
                    "block h-5 w-9 rounded-full transition-colors",
                    disabled
                        ? "bg-slate-200 dark:bg-slate-700"
                        : cx("bg-slate-300 dark:bg-slate-700", SWITCH_TONE[tone] ?? SWITCH_TONE.emerald),
                )}
            />
            <span className="pointer-events-none absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
        </span>
    );

    if (!label) {
        return (
            <label
                htmlFor={inputId}
                className={cx(disabled ? "cursor-not-allowed" : "cursor-pointer", className)}
            >
                {control}
            </label>
        );
    }

    return (
        <label
            htmlFor={inputId}
            className={cx(
                "flex items-center gap-2 text-[13px] text-slate-600 select-none dark:text-slate-400",
                disabled ? "cursor-not-allowed" : "cursor-pointer",
                className,
            )}
        >
            {control}
            <span className="min-w-0">
                <span className="block">{label}</span>
                {hint && (
                    <span className="block text-[11.5px] text-slate-400 dark:text-slate-500">{hint}</span>
                )}
            </span>
        </label>
    );
});

/**
 * A setting laid out as a row: description on the left, switch on the right.
 * This is the shape the settings page uses for every toggle.
 */
export function SwitchRow({ label, hint, locked, lockedNote, tone = "emerald", ...rest }) {
    return (
        <div
            className={cx(
                "flex items-center justify-between gap-4 rounded-lg border px-3.5 py-3 transition",
                locked
                    ? "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40"
                    : "border-slate-200 dark:border-slate-800",
            )}
        >
            <span className="min-w-0">
                <span className="flex items-center gap-2">
                    <span
                        className={cx(
                            "text-[13px] font-medium",
                            locked && "text-slate-400 dark:text-slate-500",
                        )}
                    >
                        {label}
                    </span>
                    {locked && (
                        <span className="tag bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            Locked
                        </span>
                    )}
                </span>
                <span className="mt-0.5 block text-[11.5px] text-slate-400 dark:text-slate-500">
                    {locked ? lockedNote : hint}
                </span>
            </span>

            <Switch tone={tone} disabled={locked} {...rest} />
        </div>
    );
}
